import React, { useEffect, useState, useCallback, useMemo, memo, useRef } from "react";
import axios from "axios";
import { Form, Modal, Switch, Select, Skeleton, Button } from "antd";
import CustomSelect from "../../../Reuseable/Select/Select";
import InputField from "../../../Reuseable/InputField/InputField";
import NormalButton from "../../../Reuseable/NormalButton/NormalButton";
import "./AddScriptModal.scss";
import TextArea from "antd/es/input/TextArea";
import { useSelector, useDispatch } from "react-redux";
import { PlayCircleOutlined, PauseCircleOutlined } from "@ant-design/icons";
import {
  FetchPlaceholderFields,
  fetchPortfolioCampaigns,
} from "../../../assets/api/ScriptsService";
import { fetchUserFilters } from "../../../store/filters";
import sampleAudio from "../../../assets/audio/collecteaze_sample_audio.mp4";

const AddScriptModal = ({
  isOpen,
  onClose,
  onSave,
  load,
  onBefore,
  isWizard = false,
  initialPortfolioId,
  initialPortfolioName,
  initialValues,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const token = useSelector((state) => state.auth.token);
  const { filter_list } = useSelector((state) => state.filters);
  const [placeholderFields, setPlaceholderFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [selectedPlaceholder, setSelectedPlaceholder] = useState(null);
  const [dynamicCampaigns, setDynamicCampaigns] = useState([]);
  const [campaignLoading, setCampaignLoading] = useState(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const audioRef = useRef(null);
  const lastFetchedTextRef = useRef("");
  console.log(selectedPlaceholder, 'selectedPlaceholderselectedPlaceholder')

  useEffect(() => {
    audioRef.current = new Audio(sampleAudio);

    const handleEnded = () => {
      setIsPlaying(false);
    };

    audioRef.current.addEventListener("ended", handleEnded);

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeEventListener("ended", handleEnded);
      }
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setIsPlaying(false);
      lastFetchedTextRef.current = "";
    }
  }, [isOpen]);

  const playSampleAudio = useCallback(async () => {
    // If already playing, stop
    if (isPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setIsPlaying(false);
      return;
    }

    // Get current body value and apply same transform as submit
    const rawBody = form.getFieldValue("body") || "";
    const body = rawBody.replace(/\n/g, " ");

    if (!body.trim()) {
      return;
    }

    // If payload is same, play existing audio without hitting the API again
    if (body === lastFetchedTextRef.current && audioRef.current) {
      try {
        await audioRef.current.play();
        setIsPlaying(true);
        return;
      } catch (err) {
        console.error("Failed to play cached audio, retrying API:", err);
        // Fall back to hitting API if cached play fails
      }
    }

    try {
      setAudioLoading(true);

      const res = await axios.post(
        "dialer/text-to-speech/",
        { script: body },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const audioUrl = res?.data?.s3_url;

      if (!audioUrl) {
        throw new Error("No audio URL returned from API");
      }

      // Stop any existing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeEventListener("ended", () => { });
      }

      // Create new audio instance from API response URL
      const newAudio = new Audio(audioUrl);
      audioRef.current = newAudio;

      newAudio.addEventListener("ended", () => {
        setIsPlaying(false);
      });

      await newAudio.play();
      setIsPlaying(true);
      lastFetchedTextRef.current = body;
    } catch (err) {
      console.error("Sample audio playback failed:", err);
      setIsPlaying(false);
    } finally {
      setAudioLoading(false);
    }
  }, [isPlaying, form, token]);

  const portfolioOptions = useMemo(() => {
    const portfoliosData =
      filter_list?.find((item) => item?.name === "portfolios")?.filters || [];
    const options = portfoliosData.map((d) => ({
      value: d.id || d,
      label: d.name || d.key || d,
    }));

    if (
      initialPortfolioId &&
      initialPortfolioName &&
      !options.some((opt) => opt.value === initialPortfolioId)
    ) {
      options.push({
        value: initialPortfolioId,
        label: initialPortfolioName,
      });
    }
    return options;
  }, [filter_list, initialPortfolioId, initialPortfolioName]);

  useEffect(() => {
    if (!isOpen) {
      form.resetFields();
      setPlaceholderFields([]);
      setSelectedPlaceholder(null);
      setDynamicCampaigns([]);
    } else {
      dispatch(fetchUserFilters());
      if (token) {
        setLoadingFields(true);
        FetchPlaceholderFields(token)
          .then((res) => {
            let dataArray = [];

            // console.log(res?.data?.data?.field_names, "====fetchRES====");

            // The API response is structured as:
            // res.data.data.field_names
            if (
              res?.data?.data?.field_names &&
              Array.isArray(res.data.data.field_names)
            ) {
              dataArray = res.data.data.field_names;
            } else if (
              res?.data?.field_names &&
              Array.isArray(res.data.field_names)
            ) {
              dataArray = res.data.field_names;
            } else if (Array.isArray(res?.data)) {
              dataArray = res.data;
            } else if (Array.isArray(res?.data?.data)) {
              dataArray = res.data.data;
            }

            const formatLabel = (str) => {
              if (!str) return "";
              return str
                .split("_")
                .map(
                  (word) =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
                )
                .join(" ");
            };

            const options = dataArray.map((item) => {
              const strVal =
                typeof item === "string"
                  ? item
                  : item?.name || item?.key || String(item);
              return { value: strVal, label: formatLabel(strVal) };
            });

            setPlaceholderFields(options);
          })
          .catch((err) => {
            console.error("Error fetching placeholders:", err);
          })
          .finally(() => {
            setLoadingFields(false);
          });
      }
    }
  }, [isOpen, form, token]);

  const loadCampaigns = useCallback((portfolioId, shouldResetCampaign = true) => {
    if (shouldResetCampaign) {
      form.setFieldsValue({ campaign: undefined });
    }
    if (portfolioId && token) {
      setCampaignLoading(true);
      fetchPortfolioCampaigns(token, portfolioId)
        .then((res) => {
          const campaignsData = res?.data?.data?.results || [];
          const options = Array.isArray(campaignsData)
            ? campaignsData.map((c) => ({
              value: c.id,
              label: c.name || c.key,
            }))
            : [];
          setDynamicCampaigns(options);
        })
        .catch((err) => {
          console.error("Error fetching campaigns:", err);
          setDynamicCampaigns([]);
        })
        .finally(() => {
          setCampaignLoading(false);
        });
    } else {
      setDynamicCampaigns([]);
    }
  }, [form, token]);

  useEffect(() => {
    if (isOpen) {
      if (initialValues) {
        form.setFieldsValue(initialValues);
        if (initialValues.portfolio) {
          loadCampaigns(initialValues.portfolio, false);
        }
      } else if (initialPortfolioId) {
        form.setFieldsValue({ portfolio: initialPortfolioId });
        loadCampaigns(initialPortfolioId, true);
      }
    }
  }, [isOpen, initialPortfolioId, initialValues, form, loadCampaigns]);

  const handleSave = useCallback(() => {
    form
      .validateFields()
      .then((values) => {
        const transformedValues = {
          ...values,
          body: values.body?.replace(/\n/g, " "),
        };
        onSave?.(transformedValues);
      })
      .catch((info) => {
        console.error("Validation Failed:", info);
      });
  }, [form, onSave]);

  const handlePortfolioChange = useCallback((portfolioId) => {
    loadCampaigns(portfolioId, true);
  }, [loadCampaigns]);

  const handlePlaceholderSelect = useCallback((val) => {
    if (!val) return;
    const currentBody = form.getFieldValue("body") || "";
    form.setFieldsValue({ body: currentBody + `{{xbucketdata.${val}}}` });
    setSelectedPlaceholder(null); // Reset after selection to allow re-selection
  }, [form]);

  return (
    <Modal
      className="addScriptModalMainContainer"
      title="Add Script"
      open={isOpen}
      onCancel={onClose}
      footer={null}
      centered
      width={700}
    // height={"100%"}
    >
      <div className="wrappedScriptContainer">
        <Form form={form} layout="vertical">
          <div className="selectorWrapper">
            <div className="wrappedContainer">
              <Form.Item
                name="name"
                className="formItem"
                label="Name"
                rules={[{ required: true, message: "Please enter name" }]}
              >
                <InputField placeholder="Enter script name" />
              </Form.Item>
            </div>
          </div>

          <div className="selectorWrapper">
            <div className="wrappedContainer">
              <Form.Item
                name="portfolio"
                className="formItem"
                label="Portfolio"
              // rules={[{ required: true, message: "Please select portfolio" }]}
              >
                <CustomSelect
                  options={portfolioOptions || []}
                  placeholder="Select portfolio"
                  onChange={handlePortfolioChange}
                />
              </Form.Item>
            </div>
            <div className="wrappedContainer">
              <Form.Item
                name="campaign"
                className="formItem"
                label="Campaign"
              // rules={[{ required: true, message: "Please select campaign" }]}
              >
                <CustomSelect
                  options={
                    campaignLoading
                      ? [
                        {
                          label: <Skeleton.Input active size="small" block />,
                          value: "loading-1",
                          disabled: true,
                        },
                        {
                          label: <Skeleton.Input active size="small" block />,
                          value: "loading-2",
                          disabled: true,
                        },
                        {
                          label: <Skeleton.Input active size="small" block />,
                          value: "loading-3",
                          disabled: true,
                        },
                      ]
                      : dynamicCampaigns
                  }
                  placeholder="Select campaign"
                  loading={campaignLoading}
                />
              </Form.Item>
            </div>
          </div>

          {/* The script and the two tools that write into it belong in one
              block: the toolbar used to float, right-aligned, above a
              textarea that carried no label at all. */}
          <div className="scriptBodyField">
            <div className="scriptBodyHead">
              <span className="scriptBodyLabel">Script body</span>

              <div className="scriptBodyTools">
                <Button
                  className={`play-sample-btn ${isPlaying ? "playing" : ""}`}
                  loading={audioLoading}
                  icon={
                    !audioLoading && isPlaying ? (
                      <div className="audio-visualizer">
                        <span className="bar bar1"></span>
                        <span className="bar bar2"></span>
                        <span className="bar bar3"></span>
                      </div>
                    ) : !audioLoading ? (
                      <PlayCircleOutlined />
                    ) : null
                  }
                  onClick={playSampleAudio}
                >
                  {audioLoading
                    ? "Loading..."
                    : isPlaying
                      ? "Playing Sample"
                      : "Play Sample"}
                </Button>

                <Select
                  className="scriptPlaceholder"
                  showSearch
                  placeholder="Insert placeholder"
                  options={placeholderFields}
                  loading={loadingFields}
                  value={selectedPlaceholder}
                  onChange={handlePlaceholderSelect}
                  allowClear
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </div>
            </div>

            <Form.Item
              name="body"
              className="formItem"
              rules={[{ required: true, message: "Please enter script body" }]}
            >
              <TextArea rows={6} placeholder="Type your script here..." />
            </Form.Item>
          </div>

          {/* <div className="selectorWrapper">
            <div className="wrappedContainer">
            </div>
            <div className="wrappedContainer">
              
            </div>
          </div> */}

          <div
            className="submitBtn"
          >
            {/* the row and the inline-styled Form.Item are classes now */}
            <Form.Item
              name="is_active"
              className="formItem scriptActiveToggle"
              label="Active"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch />
            </Form.Item>

            <div className="scriptFootActions">
              {isWizard && (
                <NormalButton
                  type="default"
                  className="beforeBtn"
                  onClick={() => onBefore?.(form.getFieldsValue())}
                >
                  Before
                </NormalButton>
              )}
              {/* Cancel used to appear only in the wizard, so the plain
                  dialog had no way out but the close cross. */}
              <button type="button" className="scriptCancel" onClick={onClose}>
                Cancel
              </button>
              <NormalButton type="primary" onClick={handleSave} loading={load}>
                Submit
              </NormalButton>
            </div>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default memo(AddScriptModal);